"use client";

import { ArrowLeftIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAssistantRouteHistory } from "@/components/custom/contexts/assistantRouteHistoryContext";
import { useAuth } from "@/lib/auth/AuthContext";
import { cn } from "@/lib/utils";

type WebViewBackButtonProps = {
  mode: "header" | "sidebar";
  collapsed?: boolean;
  className?: string;
};

export function WebViewBackButton({
  mode,
  collapsed = false,
  className,
}: WebViewBackButtonProps) {
  const { t } = useTranslation();
  const { scheme } = useAuth();
  const { canGoBack, goBack } = useAssistantRouteHistory();

  if (scheme !== "Bearer") return null;

  const label = t("webview.back");
  const icon = <ArrowLeftIcon className="size-4 shrink-0" strokeWidth={1.7} />;

  const handleClick = () => {
    if (canGoBack) goBack();
  };

  if (mode === "sidebar" && !collapsed) {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={!canGoBack}
        aria-label={label}
        className={cn(
          "inline-flex h-[30px] cursor-pointer items-center gap-1.5 rounded-[7px] px-2 font-medium text-[13px] text-muted-foreground transition-colors duration-120 hover:bg-muted hover:text-foreground disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-muted-foreground",
          className,
        )}
      >
        {icon}
        <span>{label}</span>
      </button>
    );
  }

  const button = (
    <button
      type="button"
      onClick={handleClick}
      disabled={!canGoBack}
      aria-label={label}
      className={cn(
        "flex cursor-pointer items-center justify-center rounded-[7px] text-muted-foreground transition-colors duration-120 hover:bg-muted hover:text-foreground disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-muted-foreground",
        mode === "header" ? "size-7" : "size-[26px]",
        className,
      )}
    >
      {icon}
    </button>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side={mode === "header" ? "bottom" : "right"}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}
